'use client';
import MainNav from '@/components/MainNav';
import Overlay from '@/components/Overlay';
import LINK from '@/constants/LINK';
import { useCart } from '@/hooks/cart';
import useDeviceDetect from '@/hooks/deviceDetect';
import { useReactQuerySession } from '@/hooks/reactQuerySession';
import { useScroll } from '@/hooks/useScroll';
import { IconHeart } from '@/icons/IconHeart';
import { IconSearch } from '@/icons/IconSearch';
import { IconShoppingBag } from '@/icons/IconShoppingBag';
import { IconUser } from '@/icons/IconUser';
import useHeaderStore from '@/store/header';
import { ErrorBoundary } from 'react-error-boundary';

const SearchPanel = dynamic(() => import('@/components/SearchPanel'), { ssr: false });
const CartPanel = dynamic(() => import('@/components/CartPanel'), { ssr: false });
const NavPanel = dynamic(() => import('@/components/NavPanel'), { ssr: false });
const FilterPanel = dynamic(() => import('@/components/FilterPanel'), { ssr: false });
const SubNavPanel = dynamic(() => import('@/components/SubNavPanel'), { ssr: false });

import { useQueryClient } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import CustomLink from '@/components/CustomLink';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { LocationSelector } from './LocationSelector';
import { IconLocation } from '@/icons/IconLocation';
import { IArea } from '@/interfaces/store';
import { QueryKeys } from '@/enums';
import LocationModal from './LocationModal';
import { BopisPanel } from './BopisPanel';
import { AnnouncementBar } from './Homepage/AnnouncementBar/AnnouncementBar';

interface IHeaderProps {
  megaMenuResponse: IMegaMenu;
  children: React.ReactNode;
}
const Header = ({ children, megaMenuResponse }: IHeaderProps) => {
  const pathname = usePathname();
  const [auxCategories, setAuxCategories] = useState<IMegaMenuItem[]>();
  const [navChild, setNavChild] = useState<IMegaMenuItem>();
  const { isDesktop } = useDeviceDetect();
  const [useFilterPanel, setUseFilterPanel] = useState<boolean>(false);
  const [useNavPanel, setUseNavPanel] = useState<boolean>(false);
  const [useCartPanel, setUseCartPanel] = useState<boolean>(false);
  const [useSearchPanel, setUseSearchPanel] = useState<boolean>(false);
  const [useSubNavPanel, setUseSubNavPanel] = useState<boolean>(false);
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [isMyLocation, setIsMyLocation] = useState<boolean>(false);
  const selectedStore = useHeaderStore((state) => state.selectedStore);
  const openSearchPanel = useHeaderStore((state) => state.openSearchPanel);
  const navPanel = useHeaderStore((state) => state.navPanel);
  const openNavPanel = useHeaderStore((state) => state.openNavPanel);
  const banner = useHeaderStore((state) => state.banner);
  const cartPanel = useHeaderStore((state) => state.cartPanel);
  const searchPanel = useHeaderStore((state) => state.searchPanel);
  const filterPanel = useHeaderStore((state) => state.filterPanel);
  const navbarBackgroundColor = useHeaderStore((state) => state.navbarBackgroundColor);
  const navbarTextColor = useHeaderStore((state) => state.navbarTextColor);
  const myLocation = useHeaderStore((state) => state.myLocation);
  const showExpressOption = useHeaderStore((state) => state.showExpressOption);
  const showPickInStoreOption = useHeaderStore((state) => state.showPickInStoreOption);
  const isAnnouncementBarLoading = useHeaderStore((state) => state.isAnnouncementBarLoading);
  const announcementBarContent = useHeaderStore((state) => state.announcementBarContent);
  const isOverlay = useHeaderStore((state) => state.overlay);
  const { bodyScrolled, translateHeader, isAtTop } = useScroll();
  const showSearchPanel = () => {
    openSearchPanel(!searchPanel);
  };

  const showNavPanel = (paramNavChild: IMegaMenuItem) => {
    setAuxCategories(paramNavChild.Items.find((x) => x.AuxCategory)?.Items);
    setNavChild(paramNavChild);
    openNavPanel(true);
  };

  useEffect(() => {
    if (filterPanel) setUseFilterPanel(true);
    if (cartPanel) setUseCartPanel(true);
    if (searchPanel) setUseSearchPanel(true);
    if (navPanel) {
      if (!isDesktop) {
        setUseNavPanel(true);
      } else {
        setUseSubNavPanel(true);
      }
    }
  }, [searchPanel, cartPanel, filterPanel, navPanel]);

  // This useEffect handles dynamic css injection of css var = --location-bar-height
  useEffect(() => {
    const rootStyle = document.documentElement.style;
    const hasOptions = showPickInStoreOption || showExpressOption;
    const isDetailsPage = pathname.includes('product');
    const isHomepage = pathname === '/';

    rootStyle.setProperty('--location-bar-height', isHomepage || (hasOptions && isDetailsPage) ? '34.4px' : '0px');

    return () => {
      rootStyle.removeProperty('--location-bar-height');
    };
  }, [showPickInStoreOption, showExpressOption, pathname]);
  return (
    <>
      <AnnouncementBar content={announcementBarContent} isLoading={isAnnouncementBarLoading} />

      <Overlay>
        {useSearchPanel && (
          <ErrorBoundary
            onError={(error) => {
              console.error('Header: SearchPanel error', error);
            }}
            fallback={null}
          >
            <SearchPanel />
          </ErrorBoundary>
        )}
        {useFilterPanel && (
          <ErrorBoundary
            onError={(error) => {
              console.error('Header: FilterPanel error', error);
            }}
            fallback={null}
          >
            <FilterPanel />
          </ErrorBoundary>
        )}
        {useCartPanel && (
          <ErrorBoundary
            onError={(error) => {
              console.error('Header: CartPanel error', error);
            }}
            fallback={null}
          >
            <CartPanel />
          </ErrorBoundary>
        )}
        {useNavPanel && (
          <ErrorBoundary
            onError={(error) => {
              console.error('Header: NavPanel error', error);
            }}
            fallback={null}
          >
            <NavPanel className="lg:hidden" content={megaMenuResponse?.Items} />
          </ErrorBoundary>
        )}
        {<BopisPanel />}
      </Overlay>
      {pathname !== '/feedback' && (
        <div
          className={`relative ${cartPanel || searchPanel || filterPanel ? 'z-10' : 'z-[200]'} hidden bg-[#f5f5f5] font-semibold lg:block ${!isAtTop ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
        >
          {children}
        </div>
      )}

      <header
        className={`w-full max-lg:fixed lg:sticky ${navPanel ? 'nav-open' : ''} z-[100] ${banner ? 'home' : ''} static-bg`}
        style={{ top: 'var(--announcement-bar-height, 0px)' }}
        onMouseLeave={() => {
          if (isDesktop && !cartPanel) {
            openNavPanel(false);
            setNavChild(undefined);
            setAuxCategories(undefined);
          }
        }}
      >
        <div
          className={`icon-hover group container relative z-[5] flex h-[var(--header-height)] items-center gap-2 text-xl transition-all hover:bg-white hover:text-black max-lg:bg-white ${
            bodyScrolled
              ? pathname == '/'
                ? `custom-menu-shadow bg-black text-white`
                : 'custom-menu-shadow bg-white !duration-[50ms]'
              : pathname == '/'
                ? `custom-menu bg-black text-white`
                : 'bg-white !duration-[50ms]'
          } ${navPanel ? 'mobile-custom-menu !bg-white !text-black' : ''} `}
          style={translateHeader && !navPanel ? { transform: 'translateY(calc(-1 * var(--header-height)))' } : {}}
        >
          <div
            className={`grid w-full grid-flow-col items-center lg:w-fit lg:grid-cols-1 ${
              pathname === '/feedback' ? 'grid-cols-1 place-items-center' : 'grid-cols-3'
            }`}
          >
            {pathname != '/feedback' && (
              <div className="w-7 lg:hidden">
                <span className="nav-toggler pc:hidden flex cursor-pointer flex-col justify-between" onClick={() => openNavPanel(!navPanel)}></span>
              </div>
            )}
            <CustomLink prefetch={false} href="/" className="mx-auto lg:ml-0 lg:mr-6 xl:mr-12">
              <div className="relative h-12 w-[6.5rem] min-w-[6.5rem]">
                <Image
                  src={'https://storage.apex4u.com/apex_logo_website_170x80.png'}
                  alt="logo"
                  fill
                  className={`object-cover ${pathname == '/' ? 'icon-hover-initial' : ''} ${
                    navPanel || (bodyScrolled && !isDesktop) ? '!block' : ''
                  }`}
                  loading="eager"
                  unoptimized={true}
                />
                <Image
                  src={'https://storage.apex4u.com/apex_logo_website_170x80.png'}
                  alt="logo"
                  fill
                  className={`object-cover ${pathname == '/' ? 'icon-hover-none' : 'hidden'} ${
                    navPanel || (bodyScrolled && !isDesktop) ? '!hidden' : ''
                  }`}
                  loading="eager"
                  unoptimized={true}
                />
              </div>
            </CustomLink>
            {pathname != '/feedback' && (
              <div className="flex items-center justify-end space-x-8 lg:hidden">
                <CartElement
                  className={`icon-hover-black ${
                    bodyScrolled
                      ? pathname == '/'
                        ? `text-black`
                        : 'text-black !duration-[50ms]'
                      : pathname == '/'
                        ? `text-white`
                        : 'text-black !duration-[50ms]'
                  }`}
                />
                <div onClick={showSearchPanel} className="cursor-pointer">
                  <IconSearch
                    className={`icon-hover-black w-7 ${
                      bodyScrolled
                        ? pathname == '/'
                          ? `text-black`
                          : '!text-black !duration-[50ms]'
                        : pathname == '/'
                          ? `text-white`
                          : 'text-black !duration-[50ms]'
                    }`}
                  />
                </div>
              </div>
            )}
          </div>
          {pathname != '/feedback' && (
            <>
              <MainNav
                content={megaMenuResponse?.Items}
                className="hidden items-center space-x-5 lg:flex 2xl:space-x-8"
                onNavSelection={showNavPanel}
                navChild={navChild}
                navPanel={navPanel}
              />
              <div className="flex items-center space-x-4 pl-4 lg:ml-auto xl:space-x-5 xl:pl-6">
                {showExpressOption && (pathname === '/' || pathname.includes('/product')) && (
                  <ExpressOption
                    pathname={pathname}
                    myLocation={myLocation}
                    setIsMyLocation={setIsMyLocation}
                    setShowLocationModal={setShowLocationModal}
                  />
                )}
                <div onClick={showSearchPanel} className="cursor-pointer max-lg:hidden">
                  <IconSearch className="w-7" />
                </div>
                <CustomLink prefetch={false} href={LINK.WISHLIST.url} className="max-lg:hidden">
                  <IconHeart className="w-7" />
                </CustomLink>
                <CartElement className="max-lg:hidden" />
                <CustomLink prefetch={false} href={LINK.MY_ACCOUNT.url} className="max-lg:hidden">
                  <IconUser className="w-7" />
                </CustomLink>
              </div>
            </>
          )}
        </div>
        <div className="relative z-[1]">
          {useSubNavPanel && (
            <ErrorBoundary
              onError={(error) => {
                console.error('Header: SubNavPanel error', error);
              }}
              fallback={null}
            >
              <SubNavPanel className="max-lg:hidden" auxCategories={auxCategories} content={navChild} />
            </ErrorBoundary>
          )}
        </div>
        {(showPickInStoreOption || showExpressOption) && (pathname === '/' || pathname.includes('/product')) && !isOverlay && (
          <div
            className="border-grey-100 location-selector-shadow z-[39] space-x-2 border-y bg-white transition-all duration-300 lg:hidden"
            style={{
              height: 'var(--location-bar-height)',
              position: 'fixed',
              top: 'calc(var(--announcement-bar-height, 0px) + var(--header-height))',
              left: 0,
              right: 0,
            }}
          >
            <div className="flex h-full items-center justify-between bg-[#F5F7FA] px-4">
              {/* Store selector */}
              {showPickInStoreOption && (
                <LocationSelector
                  isHeader
                  isSelected={Boolean(selectedStore.Name)}
                  text={selectedStore?.Name ? selectedStore.Name : 'Select Pick-Up Store'}
                  onClick={() => {
                    setIsMyLocation(false);
                    setShowLocationModal(true);
                  }}
                />
              )}
              {showPickInStoreOption && showExpressOption && <div className="h-3/4 w-px bg-[#E1E4EA]"></div>}
              {/* Thana / Area selector */}
              {showExpressOption && (
                <LocationSelector
                  isHeader
                  isSelected={Boolean(myLocation.label)}
                  text={myLocation.label ? myLocation.label : 'Select My Location'}
                  onClick={() => {
                    setIsMyLocation(true);
                    setShowLocationModal(true);
                  }}
                />
              )}
            </div>
          </div>
        )}
      </header>

      <LocationModal
        showLocationModal={showLocationModal}
        setShowLocationModal={setShowLocationModal}
        isMyLocation={isMyLocation}
        isBopis={!isMyLocation}
      />

      <style jsx global>{`
        header,
        header *:not(a, .a) {
          transition:
            background 300ms,
            transform 300ms,
            color 50ms;
        }
        body:not(.scrolled) header.home:not(.nav-open, .static-bg, :hover) {
          background-color: transparent;
          box-shadow: none !important;
        }
        body:not(.scrolled) header.home:not(.nav-open, .static-bg, :hover) .container {
          color: #fff;
        }
        body:not(.scrolled) header.home:not(.nav-open, .static-bg, :hover) .search-box input {
          box-shadow: 0 0 0 1.5px #fff;
        }
        body:not(.scrolled) header.home:not(.nav-open, .static-bg, :hover) .search-box input::placeholder {
          color: #fff;
          opacity: 1;
        }
        body:not(.scrolled) header.home:not(.nav-open, .static-bg, :hover) .search-box input:-ms-input-placeholder {
          color: #fff;
        }
        body:not(.scrolled) header.home:not(.nav-open, .static-bg, :hover) .search-box input::-ms-input-placeholder {
          color: #fff;
        }
        .search-box input {
          box-shadow: 0 0 0 1.5px #000;
        }
        .nav-toggler {
          width: 20px;
          height: 15px;
          transition: all 0.4s;
        }
        .nav-toggler:before,
        .nav-toggler:after,
        .nav-toggler span {
          display: block;
          height: 3px;
          background-color: black;
          opacity: 1;
          transition: all 0.2s;
          border-radius: 20px;
        }

        .custom-menu .nav-toggler:before,
        .custom-menu .nav-toggler:after,
        .custom-menu .nav-toggler span {
          background-color: ${navbarTextColor};
        }

        .custom-menu:hover .nav-toggler:before,
        .custom-menu:hover .nav-toggler:after,
        .custom-menu:hover .nav-toggler span,
        .mobile-custom-menu .nav-toggler:before,
        .mobile-custom-menu .nav-toggler:after,
        .mobile-custom-menu .nav-toggler span {
          background-color: black;
        }

        .nav-toggler:before {
          width: 40%;
        }
        .nav-toggler:after {
          width: 70%;
        }
        .nav-toggler span {
          width: 100%;
        }
        .nav-toggler:before,
        .nav-toggler:after {
          content: '';
        }

        .icon-hover:hover .icon-hover-black {
          color: black !important;
        }

        .icon-hover:hover .icon-hover-none,
        .icon-hover-initial {
          display: none;
        }

        .icon-hover:hover .icon-hover-initial {
          display: block;
        }
      `}</style>
      <style jsx global>{`
        .nav-open .nav-toggler {
          transform: rotate(90deg);
        }
        .nav-open .nav-toggler:before,
        .nav-open .nav-toggler:after {
          width: 100%;
        }
        .nav-open .nav-toggler:before {
          transform: translate(0, 6px) rotate(45deg);
        }
        .nav-open .nav-toggler:after {
          transform: translate(0, -6px) rotate(-45deg);
        }
        .nav-open .nav-toggler span {
          opacity: 0 !important;
        }
        .custom-menu:not(:hover) {
          background-color: ${navbarBackgroundColor};
          color: ${navbarTextColor};
        }

        .location-selector-shadow {
          box-shadow: 0 10px 15px rgb(0 0 0/12%);
        }
        ${showPickInStoreOption ||
        (showExpressOption &&
          `
          header:not(.nav-open) .custom-menu-shadow {
          box-shadow: 0 10px 15px rgb(0 0 0/12%);
        }
          `)}
      `}</style>
    </>
  );
};
const CartElement = ({ className }: { className: string }) => {
  const authObject = useReactQuerySession({ queryConfig: { queryKey: [QueryKeys.layoutBoundarySession] } });
  const queryClient = useQueryClient();
  const { cartPanel, openCartPanel, selectedStore } = useHeaderStore();
  const { data: cartData } = useCart(queryClient, authObject?.session, [selectedStore?.Code]);

  const pathname = usePathname();
  const router = useRouter();

  return (
    <div
      className={`relative ${className}`}
      onClick={() => {
        if (
          pathname !== '/cart' &&
          pathname !== '/checkout' &&
          pathname !== '/checkout/details/click-n-collect' &&
          pathname !== '/checkout/details/home-delivery' &&
          pathname !== '/checkout/payment/cash-on-delivery' &&
          pathname !== '/checkout/payment/online-payment'
        ) {
          openCartPanel(!cartPanel);
        } else if (pathname !== '/checkout/details/home-delivery') {
          router.replace('/checkout/details/home-delivery');
        }
      }}
    >
      <IconShoppingBag className={`w-7 cursor-pointer`} />
      {!!cartData?.Items?.length && (
        <div className="absolute -top-[10px] left-[20px] text-center">{cartData?.Items.length > 99 ? '99+' : cartData?.Items.length}</div>
      )}
    </div>
  );
};

const ExpressOption = ({
  pathname,
  setIsMyLocation,
  setShowLocationModal,
  myLocation,
}: {
  pathname: string;
  myLocation: IArea | null;
  setIsMyLocation: (val: boolean) => void;
  setShowLocationModal: (val: boolean) => void;
}) => {
  return (
    <div
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();

        setIsMyLocation(true);
        setShowLocationModal(true);
      }}
      className="flex cursor-pointer items-center gap-1 max-lg:hidden"
    >
      <IconLocation className="w-7" />

      <div className="whitespace-nowrap">
        <p className={`text-sm ${pathname === '/' ? '' : 'text-gray-500'} group-hover:text-gray-500`}>My Location</p>
        <p className="text-sm">{myLocation?.label || 'Select Location'}</p>
      </div>
    </div>
  );
};
export default Header;
